// Iter 145m — Public sharable Track Record snapshot page.
//
// Anonymous-accessible (no auth) so the URL travels well on social.
// OG image preview makes the link unfurl with a 1200×630 stats card.

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Loader2 } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";

const tone = (hr) =>
  hr >= 65
    ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/[0.04]"
    : hr >= 50
    ? "text-amber-300 border-amber-500/30 bg-amber-500/[0.04]"
    : "text-rose-300 border-rose-500/30 bg-rose-500/[0.04]";

const TrackRecordSharePage = () => {
  const { date } = useParams();
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/share/track-record/${date}`)
      .then((r) => { if (!cancelled) setSnap(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  const ogUrl = `${API}/share/track-record/${date}/og.png`;
  const pageUrl = typeof window !== "undefined"
    ? window.location.href
    : `https://ndakubet.com/share/${date}`;
  const hr = snap?.hit_rate ?? null;

  // Pretty-date for display.
  let prettyDate = date;
  try {
    prettyDate = new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  } catch {}

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>{snap ? `NDAKUBET · Track Record · ${prettyDate}` : "NDAKUBET · Track Record"}</title>
        <meta name="description" content={snap
          ? `On ${prettyDate}, NDAKUBET called ${snap.markets_correct ?? 0} of ${snap.markets_graded ?? 0} markets correctly (${hr ?? "—"}% hit rate).`
          : "NDAKUBET — Football betting predictions track record."} />
        {/* OG tags for social unfurl */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={snap ? `${hr ?? "—"}% hit rate · ${prettyDate}` : "NDAKUBET Track Record"} />
        <meta property="og:description" content={snap
          ? `${snap.markets_correct ?? 0}/${snap.markets_graded ?? 0} markets correct across ${snap.total_fixtures ?? 0} fixtures.`
          : ""} />
        <meta property="og:image" content={ogUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogUrl} />
      </Helmet>

      <header className="border-b border-white/10 bg-[#0d1117]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-2xl text-cyan-400" data-testid="share-logo">
            NDAKUBET<span className="text-muted-foreground font-normal text-lg"> · Track Record</span>
          </Link>
          <Link
            to="/"
            className="text-sm font-bold text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
            data-testid="share-cta"
          >
            See today's picks <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10">
        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground py-12" data-testid="share-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading snapshot…
          </div>
        )}

        {err && !loading && (
          <Card className="border-white/10" data-testid="share-empty">
            <CardContent className="p-10 text-center">
              <p className="text-lg font-bold mb-1">No snapshot for {prettyDate}</p>
              <p className="text-sm text-muted-foreground">
                Try a different date or head back to the live picks.
              </p>
            </CardContent>
          </Card>
        )}

        {snap && (
          <>
            <header className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-bold mb-1">Track Record</p>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="share-date">
                {prettyDate}
              </h1>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <Card className={`border ${tone(hr ?? 0)}`} data-testid="share-hit-rate-card">
                <CardContent className="p-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Hit rate</p>
                  <p className="text-6xl font-black font-tabular mt-2" data-testid="share-hit-rate">
                    {hr != null ? `${hr}%` : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {snap.markets_correct ?? 0} / {snap.markets_graded ?? 0} markets correct
                  </p>
                </CardContent>
              </Card>
              <Card className="border-white/10">
                <CardContent className="p-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Fixtures</p>
                  <p className="text-6xl font-black font-tabular mt-2">{snap.total_fixtures ?? 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {snap.settled_fixtures ?? 0} settled
                  </p>
                </CardContent>
              </Card>
              <Card className="border-white/10">
                <CardContent className="p-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Top picks</p>
                  <p className="text-6xl font-black font-tabular mt-2">{snap.leaderboard.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">on the leaderboard</p>
                </CardContent>
              </Card>
            </div>

            <h2 className="font-display text-2xl font-black mb-3">Top picks of the day</h2>
            <div className="space-y-3" data-testid="share-leaderboard">
              {snap.leaderboard.map((row, i) => {
                const flag = countryToFlag(row.country) || "🌍";
                const won = row.won;
                const lost = row.settled - row.won;
                return (
                  <Card key={i} className={`border ${tone(row.hit_rate ?? 0)}`} data-testid={`share-leaderboard-row-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-sm text-muted-foreground mb-1">
                            <span className="mr-1">{flag}</span>
                            {row.league}
                          </div>
                          <div className="text-lg font-bold truncate">
                            {row.home} <span className="text-muted-foreground font-normal">vs</span> {row.away}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-black font-tabular">
                            {row.hit_rate != null ? `${row.hit_rate}%` : "—"}
                          </div>
                          <div className="text-[11px] text-muted-foreground font-tabular">
                            {won} won · {lost} lost
                          </div>
                        </div>
                      </div>
                      {/* Per-market chips */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {row.markets.slice(0, 6).map((m, j) => (
                          <Badge
                            key={j}
                            variant="outline"
                            className={[
                              "text-[10px] py-1 px-2",
                              m.outcome === true
                                ? "border-emerald-500/40 text-emerald-200 bg-emerald-500/[0.06]"
                                : "border-rose-500/40 text-rose-200 bg-rose-500/[0.06]",
                            ].join(" ")}
                          >
                            {m.outcome === true ? "✓ " : "✗ "}
                            {m.market_type}: {m.prediction} · {Math.round(m.confidence)}%
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {snap.leaderboard.length === 0 && (
                <Card className="border-white/10">
                  <CardContent className="p-6 text-sm text-muted-foreground">
                    No settled fixtures yet for this date.
                  </CardContent>
                </Card>
              )}
            </div>

            <footer className="mt-12 pt-6 border-t border-white/10 text-center text-sm text-muted-foreground">
              <p>
                Predictions tracked daily.{" "}
                <Link to="/history" className="text-cyan-300 font-bold hover:text-cyan-200">
                  See full archive →
                </Link>
              </p>
              <p className="mt-2 text-xs">Proof, not promises.</p>
            </footer>
          </>
        )}
      </main>
    </div>
  );
};

export default TrackRecordSharePage;
